import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Printable EPOS receipt — 80mm thermal-printer-friendly layout.
 * Includes QR codes for:
 *   - day passes (entry QR, valid until midnight next day)
 *   - prepaid memberships (welcome QR pointing to member hub activation)
 *
 * Opens in its own window. Auto-triggers browser Print dialog.
 * Renders cleanly on standard A4 and on 58/80mm thermal paper
 * via @media print { @page { size: 80mm auto; margin: 0 } }
 */
export default function EposReceipt() {
  const { txnId } = useParams();
  const [r, setR] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('uf_token') || localStorage.getItem('epos_token') || localStorage.getItem('token');
    fetch(`${API}/api/epos/receipt/${txnId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(async res => {
        if (!res.ok) { setErr((await res.json()).detail || 'Receipt not found'); return; }
        setR(await res.json());
      })
      .catch(() => setErr('Could not load receipt'));
  }, [txnId]);

  useEffect(() => {
    if (r) {
      const t = setTimeout(() => window.print(), 400);
      return () => clearTimeout(t);
    }
  }, [r]);

  if (err) return <div style={{ padding: 40, fontFamily: 'monospace' }}>{err}</div>;
  if (!r) return <div style={{ padding: 40, fontFamily: 'monospace' }}>Loading receipt…</div>;

  const isRefund = r.txn_type === 'refund';
  const date = r.created_at ? new Date(r.created_at).toLocaleString('en-GB') : '';

  return (
    <div className="receipt-root">
      <style>{`
        @page { size: 80mm auto; margin: 0; }
        @media print {
          html, body { width: 80mm !important; margin: 0; padding: 0; background: #fff; }
          .no-print { display: none !important; }
        }
        .receipt-root {
          font-family: 'Courier New', 'Menlo', monospace;
          font-size: 12px; color: #000; background: #fff;
          width: 80mm; max-width: 80mm;
          margin: 0 auto; padding: 8px 6px;
          line-height: 1.3;
        }
        .r-center { text-align: center; }
        .r-bold { font-weight: 700; }
        .r-lg { font-size: 14px; }
        .r-xl { font-size: 18px; font-weight: 700; letter-spacing: 0.06em; }
        .r-hr { border: 0; border-top: 1px dashed #000; margin: 6px 0; }
        .r-row { display: flex; justify-content: space-between; gap: 6px; }
        .r-muted { color: #444; font-size: 10.5px; }
        .r-qr-box { text-align: center; margin: 8px 0 4px; padding: 6px; border: 1px dashed #000; }
        .r-qr-caption { font-size: 10.5px; margin-top: 4px; }
        .no-print { text-align: center; padding: 20px; background: #f3f3f3; font-family: sans-serif; }
        .no-print button { padding: 10px 20px; margin: 0 6px; border: 1px solid #333; background: #fff; cursor: pointer; }
      `}</style>

      <div className="no-print">
        <button onClick={() => window.print()}>Print Receipt</button>
        <button onClick={() => window.close()}>Close</button>
      </div>

      {/* Brand */}
      <div className="r-center">
        <div className="r-xl">ULTRAFLEX</div>
        <div className="r-muted">{r.site_name}</div>
        {r.site_address && <div className="r-muted">{r.site_address}</div>}
        {r.site_vat_number && <div className="r-muted">VAT {r.site_vat_number}</div>}
      </div>

      <hr className="r-hr" />

      {/* Txn header */}
      <div className="r-center r-bold">{isRefund ? 'REFUND' : r.txn_type === 'void' ? 'VOIDED' : 'SALE RECEIPT'}</div>
      <div className="r-row"><span>{date}</span><span>{r.staff_name}</span></div>
      <div className="r-center r-bold" style={{ marginTop: 4 }}>{r.txn_number}</div>
      {r.member_name && <div className="r-center r-muted">Member: {r.member_name}</div>}

      <hr className="r-hr" />

      {/* Items */}
      {r.items.map((it, idx) => (
        <div key={idx} style={{ marginBottom: 4 }}>
          <div className="r-row">
            <span>{it.name}</span>
            <span>£{(isRefund ? -it.line_total : it.line_total).toFixed(2)}</span>
          </div>
          <div className="r-muted">{it.quantity} x £{it.unit_price.toFixed(2)}{it.vat_category === 'exempt' ? ' (exempt)' : it.vat_category === 'zero_rated' ? ' (0%)' : ''}</div>
          {it.discount_amount > 0 && <div className="r-muted">&nbsp;&nbsp;Discount: -£{it.discount_amount.toFixed(2)}</div>}
        </div>
      ))}

      <hr className="r-hr" />

      <div className="r-row"><span>Subtotal</span><span>£{r.subtotal.toFixed(2)}</span></div>
      {r.discount_total > 0 && <div className="r-row"><span>Discounts</span><span>-£{r.discount_total.toFixed(2)}</span></div>}
      <div className="r-row"><span>VAT</span><span>£{r.vat_total.toFixed(2)}</span></div>
      <div className="r-row r-bold r-lg" style={{ marginTop: 3 }}><span>TOTAL {isRefund ? '(refunded)' : ''}</span><span>£{r.total.toFixed(2)}</span></div>

      <hr className="r-hr" />

      {/* Payments */}
      {r.payments.map((p, i) => (
        <div key={i} className="r-row">
          <span style={{ textTransform: 'capitalize' }}>{p.method.replace('_', ' ')}{p.card_last4 ? ` •••• ${p.card_last4}` : ''}</span>
          <span>£{p.amount.toFixed(2)}</span>
        </div>
      ))}
      {r.payments.some(p => p.change_given > 0) && (
        <div className="r-row r-muted"><span>Change</span><span>£{r.payments.reduce((s, p) => s + (p.change_given || 0), 0).toFixed(2)}</span></div>
      )}

      {/* Day pass QR */}
      {r.day_passes?.length > 0 && (
        <>
          <hr className="r-hr" />
          {r.day_passes.map((dp, i) => (
            <div key={i} className="r-qr-box">
              <div className="r-bold r-lg" style={{ marginBottom: 2 }}>DAY PASS</div>
              <div className="r-muted">{dp.first_name} {dp.last_name}</div>
              <div className="r-muted" style={{ marginBottom: 6 }}>Valid: {dp.visit_date}</div>
              <QRCodeSVG value={dp.qr_token} size={160} level="M" includeMargin={true} />
              <div className="r-qr-caption">Scan at the turnstile to enter.<br/>Valid until midnight.</div>
            </div>
          ))}
        </>
      )}

      {/* Prepaid membership welcome block */}
      {r.prepaid_memberships?.length > 0 && (
        <>
          <hr className="r-hr" />
          {r.prepaid_memberships.map((pm, i) => {
            const hubUrl = `${window.location.origin}/hub?welcome=${pm.member_number}`;
            return (
              <div key={i} className="r-qr-box">
                <div className="r-bold r-lg">WELCOME, {pm.member_name?.toUpperCase()}</div>
                <div className="r-muted">Member: {pm.member_number}</div>
                <div className="r-muted">{pm.months}-month access</div>
                <div className="r-muted" style={{ marginBottom: 6 }}>Until {pm.end_date}</div>
                <QRCodeSVG value={hubUrl} size={150} level="M" includeMargin={true} />
                <div className="r-qr-caption">Scan to set password,<br/>complete PAR-Q &amp; add to wallet.</div>
              </div>
            );
          })}
        </>
      )}

      <hr className="r-hr" />

      <div className="r-center r-muted" style={{ marginTop: 6 }}>
        Thank you for choosing UltraFlex.<br/>
        Questions? hello@ultraflexgym.co.uk<br/>
        ultraflexgym.co.uk
      </div>

      {/* Spacer so thermal cutter has room */}
      <div style={{ height: 40 }} />
    </div>
  );
}
